'use client';

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'antd';
import classNames from 'classnames';
import CircleButton from '@src/components/Buttons/CircleButton';
import ComponentBlocker from '@src/components/ComponentBlocker';
import styles from './styles.module.less';

/**
 * @typedef {Object} DropdownButtonOption
 * @property {React.ElementType} [icon]
 * @property {string} [iconName]
 * @property {string} [iconNameText]
 * @property {string} label
 * @property {boolean} [danger]
 * @property {string} [type]
 * @property {Function} onClick
 * @property {Function} action
 * @property {string[]} [allowedRole]
 * @property {string} [divId]
 * @property {boolean} [hidden]
 * @property {boolean} [disabled]
 */

/**
 * @typedef {Object} CircularButtonDropdownProps
 * @property {React.ElementType} [triggerIcon]
 * @property {string} [triggerIconName]
 * @property {string} [triggerIconNameText]
 * @property {DropdownButtonOption[]} options
 * @property {string} [placement]
 * @property {string} [triggerClassName]
 * @property {string} [triggerType]
 */

const PLACEMENTS = [
  'topLeft',
  'topCenter',
  'topRight',
  'bottomLeft',
  'bottomCenter',
  'bottomRight',
  'top',
  'bottom',
];

/**
 * CircularButtonDropdown
 *
 * @param {Object} props
 * @param {React.ElementType} [props.triggerIcon]
 * @param {string} [props.triggerIconName]
 * @param {string} [props.triggerIconNameText]
 * @param {DropdownButtonOption[]} props.options
 * @param {string} [props.placement]
 * @param {string} [props.triggerClassName]
 * @param {string} [props.triggerType]
 */
const CircularButtonDropdown = ({
  triggerIcon: TriggerIcon,
  triggerIconName,
  triggerIconNameText,
  options,
  placement = 'bottomCenter',
  triggerClassName = '',
  triggerType = 'default',
}) => {
  const [open, setOpen] = useState(false);

  const visibleOptions = options.filter(option => !option.hidden);

  const handleMenuClick = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    const index = parseInt(key, 10);
    const option = visibleOptions[index];
    if (option) {
      const actionHandler = option.onClick || option.action;
      if (actionHandler) {
        actionHandler(domEvent);
      }
    }
    setOpen(false);
  };

  const renderButtonContent = (option, handleItemClick) => {
    const buttonContent = () => {
      if (option.iconName || option.iconNameText) {
        return (
          <div onClick={handleItemClick} className={styles.menuItemWrapper}>
            <CircleButton
              iconName={option.iconName}
              iconNameText={option.iconNameText}
              type={option.danger ? 'danger' : option.type || 'default'}
              tooltipText={option.label}
              iconSize="sm"
              className={option.className}
              onClick={handleItemClick}
              disabled={option.disabled}
            />
          </div>
        );
      }

      if (option.icon) {
        return (
          <div onClick={handleItemClick} className={styles.iconItemWrapper}>
            <option.icon size={16} />
          </div>
        );
      }

      return (
        <div onClick={handleItemClick} className={styles.menuItemWrapper}>
          <CircleButton
            iconName="circle"
            type={option.danger ? 'danger' : option.type || 'default'}
            tooltipText={option.label}
            iconSize="sm"
            className={option.className}
            onClick={handleItemClick}
            disabled={option.disabled}
          />
        </div>
      );
    };

    if (option.allowedRole && option.allowedRole.length > 0) {
      return (
        <ComponentBlocker allowedRoles={option.allowedRole} divId={option.divId}>
          {buttonContent()}
        </ComponentBlocker>
      );
    }

    return buttonContent();
  };

  const menuItems = visibleOptions.map((option, index) => {
    const handleItemClick = e => {
      e.stopPropagation();
      const actionHandler = option.onClick || option.action;
      if (actionHandler) {
        actionHandler(e);
      }
      setOpen(false);
    };

    return {
      key: String(index),
      label: renderButtonContent(option, handleItemClick),
    };
  });

  const renderTriggerIcon = () => {
    if (triggerIconNameText) {
      return (
        <CircleButton
          iconNameText={triggerIconNameText}
          type={triggerType}
          className={triggerClassName}
          iconSize="sm"
        />
      );
    }
    if (triggerIconName) {
      return (
        <CircleButton
          iconName={triggerIconName}
          type={triggerType}
          className={triggerClassName}
          iconSize="sm"
        />
      );
    }
    if (TriggerIcon) {
      return (
        <div className={classNames(styles.triggerIconWrapper, triggerClassName)}>
          <TriggerIcon size={24} />
        </div>
      );
    }
    return (
      <CircleButton
        iconName="more_vert"
        type={triggerType}
        className={triggerClassName}
        iconSize="sm"
      />
    );
  };

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      menu={{
        items: menuItems,
        onClick: handleMenuClick,
      }}
      placement={PLACEMENTS.includes(placement) ? placement : 'bottomCenter'}
      trigger={['click']}
      overlayClassName="circular-dropdown-overlay"
      getPopupContainer={triggerRef => triggerRef.parentNode}
    >
      {renderTriggerIcon()}
    </Dropdown>
  );
};

CircularButtonDropdown.propTypes = {
  triggerIcon: PropTypes.elementType,
  triggerIconName: PropTypes.string,
  triggerIconNameText: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.elementType,
      iconName: PropTypes.string,
      iconNameText: PropTypes.string,
      label: PropTypes.string.isRequired,
      danger: PropTypes.bool,
      type: PropTypes.string,
      className: PropTypes.string,
      onClick: PropTypes.func,
      action: PropTypes.func,
      allowedRole: PropTypes.arrayOf(PropTypes.string),
      divId: PropTypes.string,
      hidden: PropTypes.bool,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  placement: PropTypes.oneOf([
    'topLeft',
    'topCenter',
    'topRight',
    'bottomLeft',
    'bottomCenter',
    'bottomRight',
    'top',
    'bottom',
  ]),
  triggerClassName: PropTypes.string,
  triggerType: PropTypes.string,
};

export default CircularButtonDropdown;
